// Distance calculation utilities for geospatial filtering

/**
 * Calculate the distance between two points using the Haversine formula
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @returns {number} Distance in miles
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

/**
 * Check if a location is within the specified distance from a reference point
 * @param {Object} userLocation - User's location {lat, lng}
 * @param {Object} shopLocation - Shop's location {lat, lng}
 * @param {number} maxDistance - Maximum distance in miles
 * @returns {boolean} True if within distance, false otherwise
 */
export function isWithinDistance(userLocation, shopLocation, maxDistance) {
  if (!userLocation || !shopLocation || !userLocation.lat || !userLocation.lng || 
      !shopLocation.lat || !shopLocation.lng) {
    return false;
  }
  
  const distance = calculateDistance(
    userLocation.lat, 
    userLocation.lng, 
    shopLocation.lat, 
    shopLocation.lng
  );
  
  return distance <= maxDistance;
}

/**
 * Get user's current location using browser geolocation API
 * @returns {Promise<Object>} Promise that resolves to {lat, lng} or null if unavailable
 */
export function getUserLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        console.warn('Geolocation error:', error);
        resolve(null);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  });
}

/**
 * Get default distance preference if user hasn't set one
 * @returns {number} Default distance in miles (25 miles)
 */
export function getDefaultDistancePreference() {
  return 25;
}

/**
 * Validate and sanitize distance preference
 * @param {number} distance - Distance preference in miles
 * @returns {number} Sanitized distance (1-25 miles)
 */
export function sanitizeDistancePreference(distance) {
  if (distance === null || distance === undefined || isNaN(distance)) {
    return getDefaultDistancePreference();
  }
  
  // Ensure distance is between 1 and 25 miles
  return Math.max(1, Math.min(25, Math.round(distance)));
} 

/**
 * Reverse geocode coordinates to 'City, State' using Nominatim API
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<string>} City, State or fallback string
 */
export async function getCityStateFromCoords(lat, lng) {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`;
    const res = await fetch(url, { headers: { 'User-Agent': 'marketplace-app/1.0' } });
    if (!res.ok) throw new Error('Reverse geocoding failed');
    const data = await res.json();
    const city = data.address.city || data.address.town || data.address.village || data.address.hamlet || data.address.county || '';
    const state = data.address.state || data.address.region || '';
    if (city && state) return `${city}, ${state}`;
    if (state) return state;
    return 'Unknown Location';
  } catch (err) {
    console.warn('Reverse geocoding error:', err);
    return 'Unknown Location';
  }
} 